/**
 * Alshallal Pastry Company HR System - Audit Logs Page
 * View system audit logs with filtering
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import { 
  Activity, 
  Search, 
  Filter,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  RefreshCw
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useListViewState } from '../hooks/useListViewState';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Action display names
const ACTION_LABELS = {
  login: { en: 'Login', ar: 'تسجيل دخول' },
  logout: { en: 'Logout', ar: 'تسجيل خروج' },
  login_failed: { en: 'Failed Login', ar: 'فشل تسجيل الدخول' },
  password_change: { en: 'Password Change', ar: 'تغيير كلمة المرور' },
  password_reset_request: { en: 'Password Reset Request', ar: 'طلب إعادة تعيين كلمة المرور' },
  user_create: { en: 'User Created', ar: 'إنشاء مستخدم' },
  user_update: { en: 'User Updated', ar: 'تحديث مستخدم' },
  user_delete: { en: 'User Deleted', ar: 'حذف مستخدم' },
  user_role_change: { en: 'Role Changed', ar: 'تغيير الدور' },
  sensitive_data_view: { en: 'Sensitive Data View', ar: 'عرض بيانات حساسة' },
};

// Severity badge styles
const SEVERITY_STYLES = {
  info: 'bg-blue-100 text-blue-800',
  warning: 'bg-amber-100 text-amber-800',
  critical: 'bg-red-100 text-red-800',
};

const AuditLogsPage = () => {
  const { hasRole } = useAuth();
  const { t, language, isRTL } = useLanguage();
  
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // Page and filters live in the URL, so leaving this screen and coming back
  // returns to the same page with the same filters.
  const [view, setView] = useListViewState('audit-logs', {
    page: 1,
    action: 'all',
    severity: 'all',
    actor: '',
  });
  const { page, action: actionFilter, severity: severityFilter, actor: searchQuery } = view;
  const setPage = useCallback((next) => setView({ page: typeof next === 'function' ? next(page) : next }), [setView, page]);
  const setActionFilter = useCallback((value) => setView({ action: value, page: 1 }), [setView]);
  const setSeverityFilter = useCallback((value) => setView({ severity: value, page: 1 }), [setView]);
  const setSearchQuery = useCallback((value) => setView({ actor: value, page: 1 }), [setView]);

  const pageSize = 20;

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('page_size', pageSize.toString());
      
      if (actionFilter && actionFilter !== 'all') params.append('action', actionFilter);
      if (severityFilter && severityFilter !== 'all') params.append('severity', severityFilter);
      if (searchQuery) params.append('actor_id', searchQuery);

      const response = await axios.get(`${API_URL}/audit/logs?${params.toString()}`);
      
      setLogs(response.data.logs || []);
      setTotalPages(response.data.total_pages || 1);
      setTotal(response.data.total || 0);
    } catch (err) {
      console.error('Failed to fetch audit logs:', err);
      setError(err.response?.data?.detail?.error || t('error_occurred'));
    } finally {
      setLoading(false);
    }
  }, [page, actionFilter, severityFilter, searchQuery, t]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const getActionLabel = (action) => {
    const labels = ACTION_LABELS[action];
    if (labels) {
      return language === 'ar' ? labels.ar : labels.en;
    }
    return action.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const formatTimestamp = (timestamp) => {
    try {
      const date = new Date(timestamp);
      return format(date, 'PPpp');
    } catch {
      return timestamp;
    }
  };

  // Check access
  if (!hasRole('hr_manager', 'hr_admin', 'it_inf', 'super_admin')) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <p className="text-lg font-medium text-slate-900">{t('error_access_denied')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="audit-logs-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{t('audit_logs')}</h1>
          <p className="text-slate-600 mt-1">
            {isRTL 
              ? `${total} سجل إجمالي`
              : `${total} total records`
            }
          </p>
        </div>
        <Button
          variant="outline"
          onClick={fetchLogs}
          disabled={loading}
          data-testid="refresh-logs"
        >
          <RefreshCw className={cn("h-4 w-4 me-2", loading && "animate-spin")} />
          {isRTL ? 'تحديث' : 'Refresh'}
        </Button>
      </div>

      {/* Filters */}
      <Card className="bg-white border-slate-200">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4">
            {/* Action filter */}
            <div className="w-48">
              <Select value={actionFilter} onValueChange={setActionFilter}>
                <SelectTrigger data-testid="action-filter">
                  <SelectValue placeholder={isRTL ? 'كل الإجراءات' : 'All Actions'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{isRTL ? 'كل الإجراءات' : 'All Actions'}</SelectItem>
                  <SelectItem value="login">{getActionLabel('login')}</SelectItem>
                  <SelectItem value="logout">{getActionLabel('logout')}</SelectItem>
                  <SelectItem value="login_failed">{getActionLabel('login_failed')}</SelectItem>
                  <SelectItem value="user_create">{getActionLabel('user_create')}</SelectItem>
                  <SelectItem value="user_update">{getActionLabel('user_update')}</SelectItem>
                  <SelectItem value="password_change">{getActionLabel('password_change')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Severity filter */}
            <div className="w-40">
              <Select value={severityFilter} onValueChange={setSeverityFilter}>
                <SelectTrigger data-testid="severity-filter">
                  <SelectValue placeholder={isRTL ? 'كل المستويات' : 'All Severity'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{isRTL ? 'كل المستويات' : 'All Severity'}</SelectItem>
                  <SelectItem value="info">{isRTL ? 'معلومات' : 'Info'}</SelectItem>
                  <SelectItem value="warning">{isRTL ? 'تحذير' : 'Warning'}</SelectItem>
                  <SelectItem value="critical">{isRTL ? 'حرج' : 'Critical'}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Reset filters */}
            <Button
              variant="ghost"
              onClick={() => {
                setView({ action: 'all', severity: 'all', actor: '', page: 1 });
              }}
            >
              {isRTL ? 'إعادة تعيين' : 'Reset'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Error state */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}

      {/* Logs table */}
      <Card className="bg-white border-slate-200">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-start text-sm font-medium text-slate-600">
                    {t('audit_timestamp')}
                  </th>
                  <th className="px-4 py-3 text-start text-sm font-medium text-slate-600">
                    {t('audit_action')}
                  </th>
                  <th className="px-4 py-3 text-start text-sm font-medium text-slate-600">
                    {t('audit_actor')}
                  </th>
                  <th className="px-4 py-3 text-start text-sm font-medium text-slate-600">
                    {t('audit_target')}
                  </th>
                  <th className="px-4 py-3 text-start text-sm font-medium text-slate-600">
                    {t('status')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {loading ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-12 text-center">
                      <RefreshCw className="h-6 w-6 animate-spin text-slate-400 mx-auto" />
                      <p className="text-slate-500 mt-2">{t('loading')}</p>
                    </td>
                  </tr>
                ) : logs.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-12 text-center">
                      <Activity className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                      <p className="text-slate-500">{t('no_data')}</p>
                    </td>
                  </tr>
                ) : (
                  logs.map((log) => (
                    <tr 
                      key={log.id} 
                      className="hover:bg-slate-50"
                      data-testid={`audit-log-${log.id}`}
                    >
                      <td className="px-4 py-3 text-sm text-slate-600">
                        {formatTimestamp(log.timestamp)}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span className="font-medium text-slate-900">
                          {getActionLabel(log.action)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">
                        {log.actor_email || log.actor_id}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">
                        {log.target_type ? `${log.target_type}:${log.target_id?.slice(0, 8)}...` : '-'}
                      </td>
                      <td className="px-4 py-3">
                        <Badge className={SEVERITY_STYLES[log.severity] || SEVERITY_STYLES.info}>
                          {log.severity}
                        </Badge>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-600">
            {isRTL 
              ? `صفحة ${page} من ${totalPages}`
              : `Page ${page} of ${totalPages}`
            }
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1 || loading}
              data-testid="prev-page"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages || loading}
              data-testid="next-page"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditLogsPage;
